import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const ADMIN_TOKEN_KEY = 'nw_token';
export const CUSTOMER_TOKEN_KEY = 'nw_customer_token';

export const http = axios.create({ baseURL: API });

// Admin routes use the admin JWT; every other route carries the customer JWT when present.
http.interceptors.request.use((config) => {
  const url = config.url || '';
  const isAdmin = url.startsWith('/admin') || url.startsWith('/auth');
  const token = localStorage.getItem(isAdmin ? ADMIN_TOKEN_KEY : CUSTOMER_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const get = (url, params) => http.get(url, { params }).then((r) => r.data);
const post = (url, body) => http.post(url, body).then((r) => r.data);
const put = (url, body) => http.put(url, body).then((r) => r.data);
const del = (url) => http.delete(url).then((r) => r.data);

export const api = {
  // catalogue
  homepage: () => get('/homepage'),
  products: (params) => get('/products', params),
  facets: () => get('/products/facets'),
  product: (idOrSlug) => get(`/products/${encodeURIComponent(idOrSlug)}`),
  fx: () => get('/fx'),
  shippingMethods: () => get('/shipping-methods'),
  // cart & checkout
  priceCart: (payload) => post('/cart/price', payload),
  hydrateCart: (items) => post('/cart/hydrate', { items, shipping_method: 'standard', insurance: false }),
  checkout: (payload) => post('/checkout', payload),
  paymentStatus: (sid) => get(`/payments/status/${sid}`),
  // enquiries
  createQuote: (payload) => post('/quotes', payload),
  contact: (payload) => post('/contact', payload),
  newsletter: (email) => post('/newsletter', { email }),
  restockAlert: (payload) => post('/restock-alerts', payload),
  // customer accounts
  register: (payload) => post('/customers/register', payload),
  customerLogin: (payload) => post('/customers/login', payload),
  customerMe: () => get('/customers/me'),
  customerUpdate: (payload) => put('/customers/me', payload),
  customerCart: () => get('/customers/me/cart'),
  customerCartSync: (items) => put('/customers/me/cart', { items }),
  customerOrders: (params) => get('/customers/me/orders', params),
  customerAddresses: () => get('/customers/me/addresses'),
  customerAddAddress: (a) => post('/customers/me/addresses', a),
  customerUpdateAddress: (id, a) => put(`/customers/me/addresses/${id}`, a),
  customerDeleteAddress: (id) => del(`/customers/me/addresses/${id}`),
  wishlist: () => get('/customers/me/wishlist'),
  wishlistAdd: (pid) => post(`/customers/me/wishlist/${pid}`),
  wishlistRemove: (pid) => del(`/customers/me/wishlist/${pid}`),
  // admin
  login: (payload) => post('/auth/login', payload),
  me: () => get('/auth/me'),
  adminCreateProduct: (p) => post('/admin/products', p),
  adminUpdateProduct: (id, p) => put(`/admin/products/${id}`, p),
  adminBulkStock: (updates) => post('/admin/products/bulk-stock', { updates }),
  adminProductChanges: (params) => get('/admin/product-changes', params),
  adminDeleteProduct: (id) => del(`/admin/products/${id}`),
  adminOrders: () => get('/admin/orders'),
  adminUpdateOrder: (id, status) => put(`/admin/orders/${id}`, { status }),
  adminDownloadOrderPdf: async (oid, kind) => {
    const r = await http.get(`/admin/orders/${oid}/${kind}.pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `NICEWINE-${kind}-${oid}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return true;
  },
  adminQuotes: () => get('/admin/quotes'),
  adminUpdateQuote: (id, status) => put(`/admin/quotes/${id}`, { status }),
  adminContacts: () => get('/admin/contacts'),
  adminEnquiries: () => get('/admin/enquiries'),
  adminUpdateEnquiry: (kind, id, status) => put(`/admin/enquiries/${kind}/${id}`, { status }),
  adminEmails: () => get('/admin/emails'),
  adminEmailDetail: (id) => get(`/admin/emails/${id}`),
  adminEmailTest: (to, lang) => post('/admin/emails/test', { to, lang }),
  adminCustomers: () => get('/admin/customers'),
  adminEmailRetryFailed: () => post('/admin/emails/retry-failed'),
  adminRestockSummary: () => get('/admin/restock-alerts/summary'),
  adminNotifyRestock: (id) => post(`/admin/products/${id}/notify-restock`),
  adminUploadProductImage: (id, file, position = 'first') => {
    const fd = new FormData(); fd.append('file', file);
    return http.post(`/admin/products/${id}/images`, fd, { params: { position }, headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  adminRemoveProductImage: (id, url) => http.delete(`/admin/products/${id}/images`, { params: { url } }).then((r) => r.data),
  adminStats: () => get('/admin/stats'),
};

export const productPath = (p) => `/product/${p?.slug || p?.id}`;
